import React, { useState, useCallback, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { Link, useNavigate, useParams } from "react-router-dom";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import { GenderDropDownSelection } from "../../../components/CustomComponents/DropdownSelections/DropDownSelections";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import UpdateShimmer from "../../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import CountryCitySelector from "../../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import CustomerGroupSelector from "../../../components/CustomComponents/DropdownSelections/CustomerGroupSelector";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery";
import { ThreeDropdownBirthdatePicker } from "../../../components/CustomComponents/DatePicker/DatePicker";
import { useSelector } from "react-redux";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast.js";

const UpdateCustomer = () => {
  const { custGuid } = useParams();
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  
  const [compCurrency, setCompCurrency] = useState("GBP"); // Added state for CompCurrency
  const [customerData, setCustomerData] = useState({});
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [originalEmail, setOriginalEmail] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);


  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "Rs",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const surface = isDarkMode ? "#1e1e1e" : "#ffffff";
  const surfaceAlt = isDarkMode ? "#252525" : "#f8f9fa";
  const borderColor = isDarkMode ? "#333" : "#e9ecef";
  const muted = isDarkMode ? "#9aa0a6" : "#6c757d";

  const formatMoney = (value) =>
    `${getCurrencySymbol(compCurrency)}${Number(value || 0).toFixed(2)}`;

  const sectionCardStyle = {
    background: surface,
    border: `1px solid ${borderColor}`,
    borderRadius: "8px",
    padding: "10px 12px 4px",
    marginBottom: "10px",
    boxShadow: isDarkMode
      ? "0 2px 10px rgba(0,0,0,0.28)"
      : "0 2px 10px rgba(0,0,0,0.04)",
  };

  const sectionTitle = (icon, title, accent = themeColor) => (
    <div className="d-flex align-items-center gap-2 mb-2">
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: "7px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: `${accent}22`,
          color: accent,
          fontSize: 12,
        }}
      >
        <i className={`fa ${icon}`}></i>
      </span>
      <h6 className="mb-0 fw-semibold">{title}</h6>
    </div>
  );


  const fetchCompCurrency = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
      const profileData = response.data.data;
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, []);

  const fetchCustomerDetails = useCallback(async () => {
    if (!custGuid || !compID) {
      showErrorToast("Invalid customer ID or company ID");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CUSTOMER_BY_GUID, {
        params: { CompID: compID, Cust_Guid: custGuid },
      });

      const data = response.data.data || response.data;

      setCustomerData({
        ...data,
        Cust_Guid: data.cust_Guid || custGuid,
        CompID: compID,
        Cust_DOB: data.cust_DOB ? new Date(data.cust_DOB) : null,
        Cust_datetime: data.cust_datetime
          ? new Date(data.cust_datetime)
          : new Date(),
        Cust_Email: data.cust_Email || "",
        Cust_Fname: data.cust_Fname || "",
        Cust_Lname: data.cust_Lname || "",
        Cust_Phone: data.cust_Phone || "",
        Cust_Address: data.cust_Address || "",
        Cust_HouseNo: data.cust_HouseNo || "",
        Cust_PostCode: data.cust_PostCode || "",
        Cust_Mobile: data.cust_Mobile || "",
        Reference_ID: data.reference_ID || "",
        VAT_Number: data.vAT_Number || "",
        Cust_Country: data.cust_Country || "",
        Cust_City: data.cust_City ? String(data.cust_City) : "",
        Cust_Gender: data.cust_Gender || "Male",
        Cust_IsCommunicationViaEmail:
          data.cust_IsCommunicationViaEmail || false,
        Cust_IsCommunicationViaText: data.cust_IsCommunicationViaText || false,
        Cust_IsCommunicationViaPhone:
          data.cust_IsCommunicationViaPhone || false,
        Cust_Opt_In_Marketing: data.cust_Opt_In_Marketing || false,
        Cust_Reference_ID: data.cust_Reference_ID || "",
        Cust_VAT_Number: data.cust_VAT_Number || "",
        Cust_LastOrderType: data.cust_LastOrderType || "",
        Cust_AverageOrderCount: data.cust_AverageOrderCount || 0,
        Cust_AverageOrderSpent: data.cust_AverageOrderSpent || 0,
        Cust_EnableLoyalty: data.cust_EnableLoyalty || false,
        Cust_Deposit: data.cust_Deposit || 0,
        Cus_LastOrderDate: data.cus_LastOrderDate || null,
        Cust_TotalOrderCount: data.cust_TotalOrderCount || 0,
        Cust_TotalOrderSpent: data.cust_TotalOrderSpent || 0,
        Cust_TotalEarnedLoyaltyPoints: data.cust_TotalEarnedLoyaltyPoints || 0,
        Cust_TotalSpentLoyaltyPoints: data.cust_TotalSpentLoyaltyPoints || 0,
        Cust_MaxCredit: data.cust_MaxCredit || 1000,
        Cust_CurrentBalance: data.cust_CurrentBalance || 0,
        Cust_GroupID: data.cust_GroupID || null,
        Cust_Note: data.cus_Note || "",
      });

      setSelectedGroup(data.cust_GroupID || null);
      const rawCity = data.cust_City;
      if (rawCity != null && rawCity !== "") {
        const n = Number(String(rawCity).trim());
        setSelectedCity(Number.isFinite(n) ? n : String(rawCity).trim());
      } else {
        setSelectedCity(null);
      }
      setOriginalEmail(data.cust_Email || "");
    } catch (error) {
      showErrorToast("Failed to load customer details");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [custGuid, compID]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setCustomerData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  // Handle toggle switch changes
  const handleToggleChange = useCallback((checked, name) => {
    setCustomerData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle dropdown and special input changes
  const handleSpecialInputChange = useCallback((name, value) => {
    setCustomerData((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  }, []);

  // Handle birthdate change
  const handleBirthdateChange = useCallback((birthdateInfo) => {
    setCustomerData((prev) => ({
      ...prev,
      Cust_DOB: birthdateInfo.formattedDate,
    }));
  }, []);


  const submitCustomer = async (submitData) => {
    try {
    
      setLoading(true);
      submitData.CompID = compID;
      submitData.Cust_Guid = customerData.Cust_Guid || custGuid;
      submitData.cus_Note = submitData.Cust_Note;

      const response = await axiosInstance.post(
        APIs.POST_ADD_UPDATE_CUSTOMER,
        submitData
      );
      console.log("Submit Response:", response); // Log the entire response for debugging

      if (response.data.status === true) {
        showSuccessToast(
          response.data.message || "Customer Successfully Updated."
        );
        navigate("/customer/manageCustomer");
      } else {
        showErrorToast(response.data.message || "Error Updating Customer");
      }

      return response.data;

    } catch (err) {
      if (err.response) {
        showErrorToast(
          err.response.data.message || "Error submitting customer"
        );
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in customer submission");
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customerData.Cust_Fname.trim()) {
      showErrorToast("Please provide a valid first name");
      return;
    }
 
    if (customerData.Cust_Country && !selectedCity) {
      showErrorToast("Please select a city for the chosen country");
      return;
    }

    if (!selectedGroup) {
      showErrorToast("Please select a customer group");
      return;
    }

    const submitData = {
      ...customerData,
      Cust_DOB: customerData.Cust_DOB || null,
      Cust_datetime: new Date().toISOString(),
      Cust_City:
        selectedCity !== null && selectedCity !== undefined && selectedCity !== ""
          ? String(selectedCity)
          : "",
      Cust_GroupID: selectedGroup,
      cus_Note: customerData.Cust_Note,
    };

    try {
      await submitCustomer(submitData);
    } catch (error) {
      // Error handled in submitCustomer
    }
  };

  // Email editing handlers
  const handleEditEmailStart = () => {
    setOriginalEmail(customerData.Cust_Email);
    setIsEditingEmail(true);
  };

  const handleSaveEmail = async () => {
    if (!customerData.Cust_Email || !validateEmail(customerData.Cust_Email)) {
      showErrorToast("Please provide a valid email");
      setCustomerData((prev) => ({ ...prev, Cust_Email: originalEmail }));
      return;
    }

    setIsEditingEmail(false);

    try {
      setLoading(true);
      const requestBody = {
        Cust_Guid: customerData.Cust_Guid || custGuid,
        CompID: customerData.CompID,
        Cust_Email: customerData.Cust_Email,
        Cust_datetime: customerData.Cust_datetime
          ? new Date(customerData.Cust_datetime)
          : new Date(),
      };

      const response = await axiosInstance.patch(
        APIs.PATCH_UPDATE_CUSTOMER_EMAIL_ADDRESS,
        requestBody
      );

      if (response.data.status) {
        showSuccessToast(response.data.message || "Email updated successfully");
      } else {
        showErrorToast(response.data.message || "Error updating email");
      }
      return response.data;
    } catch (err) {
      if (err.response) {
        showErrorToast(err.response.data.message || "Error updating email");
      } else if (err.request) {
        showErrorToast("No response received from server");
      } else {
        showErrorToast("Error in email submission");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEmail = () => {
    setCustomerData((prev) => ({ ...prev, Cust_Email: originalEmail }));
    setIsEditingEmail(false);
  };



  useEffect(() => {
    fetchCompCurrency(); // Fetch currency on component mount
    fetchCustomerDetails();
  }, [fetchCompCurrency, fetchCustomerDetails]);


  const customerDisplayName = [customerData.Cust_Fname, customerData.Cust_Lname]
    .filter(Boolean)
    .join(" ")
    .trim();

  const onAccountDue = Number(customerData.Cust_CurrentBalance || 0);
  const storeCredit = Number(customerData.Cust_Deposit || 0);
  const maxCredit = Number(customerData.Cust_MaxCredit || 1000);
  const availableCredit = Math.max(0, maxCredit - onAccountDue);
  const creditUsedPct =
    maxCredit > 0
      ? Math.min(100, Math.max(0, (onAccountDue / maxCredit) * 100))
      : 0;

  const summaryTiles = [
    {
      key: "due",
      label: "On Account Due",
      value: onAccountDue,
      icon: "fa-file-invoice",
      accent: "#dc3545",
      sub: "Managed via ledger",
    },
    {
      key: "store",
      label: "Store Credit",
      value: storeCredit,
      icon: "fa-wallet",
      accent: "#0d6efd",
      sub: "Prepaid wallet balance",
    },
    {
      key: "max",
      label: "Max Credit",
      value: maxCredit,
      icon: "fa-gauge-high",
      accent: "#6c757d",
      sub: "Editable below",
    },
    {
      key: "spent",
      label: "Total Spent",
      value: Number(customerData.Cust_TotalOrderSpent || 0),
      icon: "fa-shopping-bag",
      accent: themeColor,
      sub: `${customerData.Cust_TotalOrderCount || 0} orders`,
    },
  ];

  return (
    <>
      {loading && <Preloader />}
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
          <div>
            <ol className="breadcrumb mb-0 small">
              <li className="breadcrumb-item">
                <Link to="/customer/manageCustomer">Customers</Link>
              </li>
              <li className="breadcrumb-item active">Update</li>
            </ol>
            <h1 className="page-header mb-0" style={{ fontSize: "1.35rem" }}>
              <i className="fa fa-user-pen me-2" style={{ color: themeColor }}></i>
              Update Customer
              {customerDisplayName ? (
                <small className="ms-2 fw-normal" style={{ color: muted }}>
                  {customerDisplayName}
                </small>
              ) : null}
            </h1>
          </div>
          <div className="ms-auto d-flex align-items-center gap-2">
            <Link
              to={`/customer/wallet/${custGuid}`}
              className="btn btn-sm px-3"
              style={{
                backgroundColor: "transparent",
                border: `1.5px solid ${themeColor}`,
                color: themeColor,
                borderRadius: "16px",
                fontWeight: "600",
                textDecoration: "none",
              }}
            >
              <i className="fa fa-wallet me-1"></i>
              Ledger
            </Link>
            <button
              className="btn btn-sm px-3"
              onClick={handleSubmit}
              disabled={loading}
              style={{
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "16px",
                fontWeight: "600",
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
            >
              <i className="fa fa-save me-1"></i>
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>

        {initialLoading ? (
          <UpdateShimmer />
        ) : (
          <>
            <div className="row g-2 mb-2">
              {summaryTiles.map((tile) => (
                <div className="col-6 col-xl-3" key={tile.key}>
                  <div
                    className="h-100 d-flex align-items-center gap-2"
                    style={{
                      background: surface,
                      border: `1px solid ${borderColor}`,
                      borderLeft: `3px solid ${tile.accent}`,
                      borderRadius: "8px",
                      padding: "8px 10px",
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "7px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `${tile.accent}22`,
                        color: tile.accent,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      <i className={`fa ${tile.icon}`}></i>
                    </span>
                    <div className="min-w-0">
                      <div className="small text-truncate" style={{ color: muted, fontSize: 11 }}>
                        {tile.label}
                      </div>
                      <div className="fw-bold lh-1" style={{ color: tile.accent, fontSize: "1.05rem" }}>
                        {formatMoney(tile.value)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2"
              style={{
                ...sectionCardStyle,
                padding: "8px 12px",
                borderLeft: `3px solid ${themeColor}`,
              }}
            >
              <div className="small" style={{ color: muted }}>
                <i className="fa fa-wallet me-1" style={{ color: themeColor }}></i>
                Balances are ledger-managed · Available {formatMoney(availableCredit)} ({creditUsedPct.toFixed(0)}% limit used)
              </div>
              <Link
                to={`/customer/wallet/${custGuid}`}
                className="btn btn-sm"
                style={{
                  backgroundColor: themeColor,
                  color: "#fff",
                  borderRadius: "14px",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Open ledger
              </Link>
            </div>

            <div style={{ ...sectionCardStyle, borderTop: `2px solid ${themeColor}` }}>
              {isEditingEmail ? (
                <div className="d-flex flex-wrap gap-2 align-items-end">
                  <div className="flex-grow-1">
                    <FormInput
                      label="Email"
                      name="Cust_Email"
                      type="email"
                      value={customerData.Cust_Email}
                      onchange={handleInputChange}
                      placeholder="Enter Email"
                      prepend={<i className="fa fa-envelope text-muted"></i>}
                    />
                  </div>
                  <button className="btn btn-success btn-sm mb-2" onClick={handleSaveEmail}>
                    <i className="fa fa-check me-1"></i>Save
                  </button>
                  <button className="btn btn-secondary btn-sm mb-2" onClick={handleCancelEmail}>
                    <i className="fa fa-times me-1"></i>Cancel
                  </button>
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-1">
                  <div className="fw-semibold" style={{ color: themeColor }}>
                    <i className="fa fa-envelope me-2"></i>
                    {customerData.Cust_Email || "No email set"}
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={handleEditEmailStart}
                    style={{
                      border: `1px solid ${themeColor}`,
                      color: themeColor,
                      background: "transparent",
                      fontWeight: 600,
                    }}
                  >
                    <i className="fa fa-pencil me-1"></i>
                    Edit
                  </button>
                </div>
              )}
            </div>

            <div style={{ ...sectionCardStyle, borderTop: "2px solid #0d6efd" }}>
              {sectionTitle("fa-user", "Personal & contact", "#0d6efd")}
              <div className="row g-2 mb-1">
                <div className="col-md-4">
                  <FormInput
                    label="First Name"
                    name="Cust_Fname"
                    value={customerData.Cust_Fname}
                    onchange={handleInputChange}
                    placeholder="Enter First Name"
                    prepend={<i className="fa fa-user text-muted"></i>}
                  />
                </div>
                <div className="col-md-4">
                  <FormInput
                    label="Last Name"
                    name="Cust_Lname"
                    value={customerData.Cust_Lname}
                    onchange={handleInputChange}
                    placeholder="Enter Last Name"
                    prepend={<i className="fa fa-user text-muted"></i>}
                  />
                </div>
                <div className="col-md-4">
                  <FormInput
                    label="Mobile Number"
                    name="Cust_Mobile"
                    placeholder="Enter Mobile Number"
                    type="text"
                    value={customerData.Cust_Mobile}
                    onchange={handleInputChange}
                    prepend={<i className="fa fa-mobile text-muted"></i>}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Date of Birth</label>
                  <ThreeDropdownBirthdatePicker
                    onDateChange={handleBirthdateChange}
                    initialDate={customerData.Cust_DOB}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Gender</label>
                  <GenderDropDownSelection
                    selectedValue={customerData.Cust_Gender}
                    onChange={(option) =>
                      handleSpecialInputChange("Cust_Gender", option?.value)
                    }
                  />
                </div>
              </div>
            </div>

            <div style={{ ...sectionCardStyle, borderTop: "2px solid #fd7e14" }}>
              {sectionTitle("fa-comments", "Communication", "#fd7e14")}
              <div className="row g-2 mb-1">
                {[
                  { name: "Cust_IsCommunicationViaEmail", label: "Email" },
                  { name: "Cust_IsCommunicationViaText", label: "Text" },
                  { name: "Cust_IsCommunicationViaPhone", label: "Phone" },
                  { name: "Cust_Opt_In_Marketing", label: "Marketing" },
                ].map((item) => (
                  <div className="col-6 col-md-3" key={item.name}>
                    <div
                      style={{
                        background: surfaceAlt,
                        border: `1px solid ${borderColor}`,
                        borderRadius: "8px",
                        padding: "6px 8px",
                      }}
                    >
                      <ToggleSwitch
                        isChecked={customerData[item.name] || false}
                        onToggle={(checked) => handleToggleChange(checked, item.name)}
                        name={item.name}
                        label={item.label}
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ ...sectionCardStyle, borderTop: "2px solid #6f42c1" }}>
              {sectionTitle("fa-map-marker-alt", "Address & group", "#6f42c1")}
              <div className="row g-2 mb-1">
                <div className="col-md-6">
                  <FormInput
                    label="Address"
                    name="Cust_Address"
                    value={customerData.Cust_Address}
                    onchange={handleInputChange}
                    placeholder="Enter Address"
                    prepend={<i className="fa fa-road text-muted"></i>}
                  />
                </div>
                <div className="col-md-3">
                  <FormInput
                    label="House Number"
                    name="Cust_HouseNo"
                    value={customerData.Cust_HouseNo}
                    onchange={handleInputChange}
                    placeholder="Enter House Number"
                    prepend={<i className="fa fa-home text-muted"></i>}
                  />
                </div>
                <div className="col-md-3">
                  <FormInput
                    label="Post Code"
                    name="Cust_PostCode"
                    value={customerData.Cust_PostCode}
                    onchange={handleInputChange}
                    placeholder="Enter Post Code"
                    prepend={<i className="fa fa-map-pin text-muted"></i>}
                  />
                </div>
              </div>
              <div className="row g-2 mb-1">
                <CountryCitySelector
                  selectedCountry={customerData.Cust_Country}
                  selectedCity={selectedCity}
                  setSelectedCity={setSelectedCity}
                  onChange={(data) => {
                    setCustomerData((prev) => ({ ...prev, ...data }));
                    if (data.Cust_Country && !data.Cust_City) {
                      setSelectedCity(null);
                    }
                  }}
                  setLoading={setLoading}
                  isDarkMode={isDarkMode}
                  selectedValueIsLabel={false}
                  countryFieldName="Cust_Country"
                  cityFieldName="Cust_City"
                />
              </div>
              <div className="row g-2 mb-1">
                <div className="col-md-6">
                  <label className="form-label">Groups</label>
                  <CustomerGroupSelector
                    selectedGroup={selectedGroup}
                    setSelectedGroup={setSelectedGroup}
                    onChange={(value) => setSelectedGroup(value)}
                    setLoading={setLoading}
                  />
                </div>
              </div>
            </div>

            <div style={{ ...sectionCardStyle, borderTop: `2px solid ${themeColor}` }}>
              {sectionTitle("fa-chart-line", "Account & stats", themeColor)}
              <div className="row g-2 mb-1">
                <div className="col-md-4">
                  <FormInput
                    label="Reference ID"
                    name="Cust_Reference_ID"
                    value={customerData.Cust_Reference_ID}
                    onchange={handleInputChange}
                    placeholder="Enter Reference ID"
                    prepend={<i className="fa fa-id-badge text-muted"></i>}
                  />
                </div>
                <div className="col-md-4">
                  <FormInput
                    label="VAT Number"
                    name="Cust_VAT_Number"
                    value={customerData.Cust_VAT_Number}
                    onchange={handleInputChange}
                    placeholder="Enter VAT Number"
                    prepend={<i className="fa fa-file-text text-muted"></i>}
                  />
                </div>
                <div className="col-md-4">
                  <FormInput
                    label={`Max Credit (${getCurrencySymbol(compCurrency)})`}
                    name="Cust_MaxCredit"
                    value={customerData.Cust_MaxCredit}
                    onchange={handleInputChange}
                    placeholder={`${getCurrencySymbol(compCurrency)}1000.00`}
                    prepend={getCurrencySymbol(compCurrency)}
                    type="number"
                  />
                </div>
                <div className="col-md-3">
                  <FormInput
                    label={`Store Credit (${getCurrencySymbol(compCurrency)})`}
                    name="Cust_Deposit"
                    value={customerData.Cust_Deposit}
                    onchange={handleInputChange}
                    placeholder={`${getCurrencySymbol(compCurrency)}0.00`}
                    prepend={getCurrencySymbol(compCurrency)}
                    type="number"
                    disabled={true}
                  />
                </div>
                <div className="col-md-3">
                  <FormInput
                    label={`On Account Due (${getCurrencySymbol(compCurrency)})`}
                    name="Cust_CurrentBalance"
                    value={customerData.Cust_CurrentBalance}
                    onchange={handleInputChange}
                    placeholder={`${getCurrencySymbol(compCurrency)}0.00`}
                    prepend={getCurrencySymbol(compCurrency)}
                    type="number"
                    disabled={true}
                  />
                </div>
                <div className="col-md-3">
                  <FormInput
                    label={`Avg Spent (${getCurrencySymbol(compCurrency)})`}
                    name="Cust_AverageOrderSpent"
                    value={customerData.Cust_AverageOrderSpent}
                    onchange={handleInputChange}
                    placeholder={`${getCurrencySymbol(compCurrency)}0.00`}
                    prepend={getCurrencySymbol(compCurrency)}
                    disabled={true}
                    type="number"
                  />
                </div>
                <div className="col-md-3">
                  <FormInput
                    label="Avg Order Count"
                    name="Cust_AverageOrderCount"
                    value={customerData.Cust_AverageOrderCount}
                    onchange={handleInputChange}
                    placeholder="0"
                    prepend={<i className="fa fa-calculator text-muted"></i>}
                    type="number"
                  />
                </div>
                <div className="col-md-3">
                  <FormInput
                    label="Total Orders"
                    name="Cust_TotalOrderCount"
                    value={customerData.Cust_TotalOrderCount}
                    onchange={handleInputChange}
                    placeholder="0"
                    prepend={<i className="fa fa-shopping-cart text-muted"></i>}
                    type="number"
                  />
                </div>
                <div className="col-md-3">
                  <FormInput
                    label={`Total Spent (${getCurrencySymbol(compCurrency)})`}
                    name="Cust_TotalOrderSpent"
                    value={customerData.Cust_TotalOrderSpent}
                    onchange={handleInputChange}
                    placeholder={`${getCurrencySymbol(compCurrency)}0.00`}
                    prepend={getCurrencySymbol(compCurrency)}
                    type="number"
                  />
                </div>
                <div className="col-md-6">
                  <FormInput
                    label="Note"
                    name="Cust_Note"
                    value={customerData.Cust_Note}
                    onchange={handleInputChange}
                    placeholder="Enter Note"
                    prepend={<i className="fa fa-sticky-note text-muted"></i>}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default UpdateCustomer;